import {ajax} from 'rxjs/ajax';
import latinize from 'latinize';
import {map, mergeMap} from 'rxjs/operators';
import {type Observable, of, Subscriber} from 'rxjs';

import RESTClient from 'web-app/api/clients/rest-client-famly-app';
import {hasValue} from 'web-app/util/typescript';
import LocalStorage from 'web-app/services/local-storage';
import {USE_ANDROID_FOREGROUND_SERVICE_WHILE_USING_CAMERA_EXPERIMENT} from 'web-app/react/components/file-upload/constants';

/**
 * Upload any file to our backend buckets
 *
 * @param {File} file - File up upload
 * @param {Optional<boolean>} forceUploadFallback - Forces the upload straight to the fallback URL
 *
 * @returns Observable<string> - an observable of the public URL of the uploaded resource
 */
export const uploadFileObservable = (
    file: File,
    forceUploadFallback?: boolean,
    onProgress?: (percentage: number) => void,
) => {
    const filename = getCleanedFileName(latinize(file.name));

    // rxjs/ajax has built-in progress updates. This serves as a bridge to a simple callback that can be provided as an argument
    const progressSubscriber = hasValue(onProgress)
        ? new Subscriber<ProgressEvent>(
              // On progress
              progressEvent => onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100)),
              // On error
              () => onProgress(-1),
              // On complete
              () => onProgress(100),
          )
        : undefined;

    const makeAjaxUpload = (url: string) =>
        /**
         * The previous implementation of file upload was built using XMLHttpRequest.
         * Luckily rxjs/ajax can be used almost interchangeably with XMLHttpRequest
         */
        ajax({
            method: 'PUT',
            headers: {
                'Content-Type': file.type,
            },
            url,
            body: file,
            async: true,
            progressSubscriber,
        });

    // Create a shared observable to request an upload URL.
    const requestUpload$: Observable<RequestResponse> = RESTClient.postAuthenticatedObservable('v2/uploads/request', {
        filename,
        // We don't want the 'false' case to appear in the query params
        ...(LocalStorage.getForUserUnencrypted(USE_ANDROID_FOREGROUND_SERVICE_WHILE_USING_CAMERA_EXPERIMENT)
            ? {experimentalUpload: 'true'}
            : null),
        // We share this observable as it would otherwise fire on each inner ajax request
    });

    return requestUpload$.pipe(
        mergeMap(request =>
            makeAjaxUpload(forceUploadFallback ? request.fallbackUrl : request.signedUrl).pipe(
                mergeMap(ajaxResponse => {
                    // I've kept these status conditions from a previous implementation
                    if (ajaxResponse.status >= 200 && ajaxResponse.status < 400) {
                        // OK
                        return of(request.publicUrl);
                    }
                    // If we haven't already forced use of the fallback URL we can use that if the original upload fails
                    if (!forceUploadFallback) {
                        return makeAjaxUpload(request.fallbackUrl).pipe(
                            map(ajaxResponse => {
                                // If the request returns anything but 200 we'll log an error
                                if (ajaxResponse.status === 200) {
                                    return request.publicUrl;
                                }
                                throw new Error(`Upload error: ${ajaxResponse.status}`);
                            }),
                        );
                    }
                    // If we've forced the use of the fallback URL then we
                    // have no sane fallback to take now so we simply
                    // report the error.
                    throw new Error(`Upload error: ${ajaxResponse.status}`);
                }),
            ),
        ),
    );
};

interface RequestResponse {
    publicUrl: string;
    fallbackUrl: string;
    signedUrl: string;
}

// Copy/paste from previous implementation
const getCleanedFileName = (filename: string) => {
    const split = filename.split('.');
    const extension = split.pop();
    const noExtension = split.join('');
    const cleaned = noExtension.replace(/[^æøå\w -]+/g, '').replace(/ /g, '-');

    return `${cleaned}.${extension}`;
};
